import { http } from '@/lib/http';
import { env } from '@/lib/env';
import type {
  AccountModeResponse,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminSymbol,
  EstimationFreezeListResponse,
  SetAccountModeRequest,
  SetAccountModeResponse,
  SetSingleNetRequest,
  SetSingleNetResponse,
  ApplyPendingNowRequest,
  ClearEmergencyStopRequest,
  ClearTradeLockRequest,
  CreateSymbolRequest,
  CtidAccountSyncResponse,
  CtidDeleteResponse,
  CtidListResponse,
  CtidOAuthCompleteRequest,
  CtidOAuthCompleteResponse,
  CtidOAuthStartRequest,
  CtidOAuthStartResponse,
  CtidRefreshTokenResponse,
  ContributionTrend,
  DashboardResponse,
  ElimConfig,
  EmergencyStopRequest,
  ForceLiquidationRequest,
  DealReconcile,
  CloseUserVirtualPositionRequest,
  CloseUserVirtualPositionResponse,
  DetachPreview,
  DetachResult,
  CloseRealPositionsRequest,
  CloseRealPositionsResponse,
  MainAccountConfigResponse,
  MainAccountPositionsResponse,
  UserVirtualPositionsResponse,
  MainAccountTrendbarsQuery,
  MainAccountTrendbarsResponse,
  Money,
  ParamPreset,
  RiskEvent,
  RiskEventList,
  RiskEventListQuery,
  SelectMainLiveAccountRequest,
  SelectMainLiveAccountResponse,
  SetPresetRequest,
  SessionWindowValidation,
  SignalList,
  SignalListQuery,
  DealList,
  DealListQuery,
  UserOrderList,
  UserOrderListQuery,
  EquityRange,
  RealAccountEquityHistory,
  SetDirectionalCapRequest,
  SetCommissionRequest,
  TotpSetupResponse,
  TotpStatusResponse,
  TradingSession,
  TradingGroup,
  TradingGroupList,
  CreateTradingGroupRequest,
  UpdateTradingGroupRequest,
  GroupDailyQuery,
  GroupDailyReport,
  UnbindTotpRequest,
  UpdateElimConfigRequest,
  UpdateSymbolRequest,
  UpdateTradingSessionRequest,
  UpdateUserRequest,
  VirtualUserList,
  VirtualUserRow,
  ResetEmployeePasswordResponse,
  VerifyTotpRequest,
  VerifyTotpResponse,
  InviteCode,
  InviteCodeList,
  CreateInviteCodeRequest,
  UpdateInviteCodeRequest,
  AppVersionConfig,
  UpdateAppVersionRequest,
} from './types';
import type {
  AdminAccount,
  AssessmentConfig,
  AssessmentOverrideRequest,
  AssessmentOverrideRevokeRequest,
  AssessmentRecordList,
  AssessmentRecordQuery,
  AuditLogList,
  AuditLogQuery,
  CtraderSymbolCatalogItem,
  DailyReportRow,
  IpWhitelistEntry,
  NotificationTemplate,
  PlatformPnlResponse,
  ProfitShareConfig,
  ProfitShareConfigUpdateResponse,
  ProfitShareMonthly,
  ProfitSharePayoutItem,
  ProfitShareUserDetail,
  UpdateProfitShareConfigRequest,
  RealAccountCapacity,
  ResetAdminPasswordResponse,
  RiskConfig,
  UpdateRiskConfigRequest,
  UserAssessmentConfig,
  WeeklyReportPayload,
} from './extras';

const P = env.adminApiPrefix;

// ─── 鉴权 ────────────────────────────────────────────────────────

export const adminAuth = {
  login: async (body: AdminLoginRequest) => (await http.post<AdminLoginResponse>(`${P}/auth/login`, body)).data,
};

// ─── 仪表板 ──────────────────────────────────────────────────────

export const adminDashboard = {
  get: async () => (await http.get<DashboardResponse>(`${P}/dashboard`)).data,
};

// ─── §8.4.5 / §9.16 当前管理员 TOTP 自助绑定/解绑 ──────────────────
//
// 绑定流程:setup()(服务端生成密钥,未启用)→ 前端【本地】渲染二维码 →
// 用户扫码输入首码 → verify({secret,code})(校验通过则落库启用 + 返回救援码)。
// 解绑:unbind({code})(校验当前码或救援码后删除)。
export const adminTotp = {
  /** 查询当前管理员 TOTP 是否已绑定 */
  status: async () => (await http.get<TotpStatusResponse>(`${P}/me/totp`)).data,
  /** 开始绑定 — 服务端生成 base32 密钥 + otpauth URI(此时尚未启用) */
  setup: async () => (await http.post<TotpSetupResponse>(`${P}/me/totp/setup`)).data,
  // verify / unbind 用 401 表达"验证码错误"这一业务校验,
  // 故设 skipAuthClear 避免触发全局自动登出。
  /** 确认绑定 — 校验首个验证码,通过则启用并返回一次性救援码 */
  verify: async (body: VerifyTotpRequest) =>
    (await http.post<VerifyTotpResponse>(`${P}/me/totp/verify`, body, { skipAuthClear: true })).data,
  /** 解绑 — 需当前验证码或救援码确认 */
  unbind: async (body: UnbindTotpRequest) =>
    (await http.delete<void>(`${P}/me/totp`, { data: body, skipAuthClear: true })).data,
};

// ─── 主账户配置(Settings 页"主账户配置"卡片,只读)──────────────────
//
// getConfig 只读展示当前主账户 + 候选。写路径已退役:改主真实账号统一走
// adminAudit.selectMainLiveAccount(/ctids 页,LIVE 校验 + 预检 + 重启复检闭环)。
export const adminMainAccount = {
  getConfig: async () =>
    (await http.get<MainAccountConfigResponse>(`${P}/main-account/config`)).data,
  // ── §9.4 资金账户大屏:主账户 K 线 + 持仓明细 ──
  // 主账户未配置/未连接时后端返 503;上层(LiveScreen)据此做"主账户未连接"降级。
  /** 主账户品种 K 线(默认 period=m1, count=200);省略 symbolId 时后端取第一个可交易品种 */
  trendbars: async (params?: MainAccountTrendbarsQuery) =>
    (await http.get<MainAccountTrendbarsResponse>(`${P}/main-account/trendbars`, { params })).data,
  /** 主账户当前持仓明细(从镜像 positions 表读;当前价/浮动 PnL 不返回,需前端叠加行情) */
  positions: async () =>
    (await http.get<MainAccountPositionsResponse>(`${P}/main-account/positions`)).data,
  /** 各用户虚拟持仓(virtual_legs;v3 主账户净仓无单一 user_id,谁持什么仓看此) */
  userPositions: async () =>
    (await http.get<UserVirtualPositionsResponse>(`${P}/user-positions`)).data,
  /** 资金账户大屏行级仓位风控平仓:用户端只收到系统风控结算提示 */
  closeUserPosition: async (legId: string, body: CloseUserVirtualPositionRequest) =>
    (await http.post<CloseUserVirtualPositionResponse>(`${P}/user-positions/${legId}/close`, body)).data,
  /**
   * 逐笔脱钩**预览**(只读):把这条腿摘出对冲池后,真实层将执行什么动作。
   * 必看 —— 真实账户是全平台每品种一个净仓,摘掉一个人后按剩下的人重新轧净,
   * 动作可能是平仓/减仓,**也可能是反向开仓**。
   */
  detachPreview: async (legId: string) =>
    (await http.get<DetachPreview>(`${P}/user-positions/${legId}/detach-preview`)).data,
  /** 逐笔脱钩(★ 不可逆):只平该用户对应的真实敞口,不动其虚拟持仓 */
  detachUserPosition: async (legId: string, body: { reason: string }) =>
    (await http.post<DetachResult>(`${P}/user-positions/${legId}/detach`, body)).data,
};

// ─── 真实账户撮合模式 HEDGING/NETTING(Settings 页"撮合模式"卡片)──────────────
//
// **热切换**:PUT 写 system_config 后 reconcile 约 3s 内生效,无需重启(与主账户切换不同)。
// ⚠ 仅在向券商申请开通 netting 通过后才切 NETTING;账户实际仍是 hedging 时切 NETTING 会累积对冲仓震荡。
export const adminSingleNet = {
  set: async (body: SetSingleNetRequest) =>
    (await http.put<SetSingleNetResponse>(`${P}/single-net-position`, body)).data,
};

export const adminAccountMode = {
  get: async () =>
    (await http.get<AccountModeResponse>(`${P}/account-mode`)).data,
  set: async (body: SetAccountModeRequest) =>
    (await http.put<SetAccountModeResponse>(`${P}/account-mode`, body)).data,
};

// ─── N4 估值失配冻结(§7.3):列出 + 人工解除 ─────────────────────
export const adminEstimationFreeze = {
  list: async () =>
    (await http.get<EstimationFreezeListResponse>(`${P}/estimation-freeze`)).data,
  clear: async (symbolId: string, note?: string) =>
    (await http.delete<{ ok: boolean; symbolId: string }>(`${P}/estimation-freeze/${symbolId}`, {
      data: { note },
    })).data,
};

// ─── v2 §13 真实账户容量监控 ─────────────────────────────────────

export const adminRealAccount = {
  /** 唯一真实 hedging 账户容量三面指标 + 分级预警(资金账户大屏 / 仪表板容量卡) */
  capacity: async () =>
    (await http.get<RealAccountCapacity>(`${P}/real-account/capacity`)).data,
  /** 真实账户净值历史折线(仪表板净值曲线 / 大屏);range=1W/1M/1Y/ALL */
  equityHistory: async (range: EquityRange) =>
    (await http.get<RealAccountEquityHistory>(`${P}/real-account/equity-history`, { params: { range } })).data,
  /**
   * 真实账户人工平仓 + 全量脱钩(不停机、不锁交易,区别于「一键强制平仓」)。
   * scope 只约束平仓范围;脱钩范围恒为此刻全部存量活跃虚拟腿,故 acknowledgeFullDetach 必须为 true。
   * 不可逆;失败明细在 failures[],剩余仓位交由净额对账自动收敛 —— 不要提供重试按钮(会与对账重复下单)。
   */
  closeRealPositions: async (body: CloseRealPositionsRequest) =>
    (await http.post<CloseRealPositionsResponse>(`${P}/real-account/positions/close`, body)).data,
};

// ─── v2 §5.1 虚拟账户用户额度 ────────────────────────────────────

export const adminUsers = {
  /** v2 用户列表(虚拟账户,替代账户池雇员)。search=按 手机号/昵称/用户ID/邀请码/渠道别名 服务端模糊过滤。 */
  list: async (params?: {
    page?: number;
    pageSize?: number;
    followMode?: 'REVERSE' | 'FOLLOW';
    status?: string;
    search?: string;
    /** 是否包含已归档(离职移出名单)用户;默认 false。 */
    includeArchived?: boolean;
  }) => (await http.get<VirtualUserList>(`${P}/users`, { params })).data,
  /** v2 用户详情 */
  detail: async (id: string) => (await http.get<VirtualUserRow>(`${P}/users/${id}`)).data,
  /**
   * 修改用户可编辑属性(当前仅「交易时段组」单用户覆盖)。
   * tradingGroupId 三态:缺省=不动 / null=清除个人覆盖(回落渠道继承或全局)/ 字符串=指派到该组。
   * 立即生效(后端会失效时段解析缓存),但不回溯已开仓的虚拟腿(组归属是开仓时快照)。
   */
  update: async (id: string, body: UpdateUserRequest) =>
    (await http.patch<VirtualUserRow>(`${P}/users/${id}`, body)).data,
  /** 切换跟单模式(REVERSE/FOLLOW,立即生效);后端走 /employees/:id/follow-mode(v2 写 virtual_accounts) */
  setFollowMode: async (id: string, followMode: 'REVERSE' | 'FOLLOW') =>
    (await http.patch<{ status: string; followMode: string }>(
      `${P}/employees/${id}/follow-mode`,
      { followMode },
    )).data,
  /** 设置 / 重置某用户虚拟额度(美元),并清零已实现盈亏 + 解除锁定 → active。balance 必填(无默认 0)。 */
  resetBalance: async (id: string, balance: number) =>
    (await http.post<{ userId: string; status: string; virtualBalance: Money }>(
      `${P}/users/${id}/reset-balance`,
      { balance },
    )).data,
  /** §9.7 重置某员工登录密码 — 生成临时密码(原密码立即失效 + must_change_password);走既有 /employees/:id/reset-password */
  resetPassword: async (id: string) =>
    (await http.post<ResetEmployeePasswordResponse>(`${P}/employees/${id}/reset-password`)).data,
  /** 暂停用户(禁交易 + 平掉其真实仓;可登录) */
  suspend: async (id: string) =>
    (await http.post<{ userId: string; status: string }>(`${P}/employees/${id}/suspend`, {})).data,
  /** 解雇用户(禁登录禁交易 + 平掉其真实仓) */
  dismiss: async (id: string) =>
    (await http.post<{ userId: string; status: string }>(`${P}/employees/${id}/dismiss`, {})).data,
  /** 恢复用户(取消暂停 / 取消停用 → active,保留余额与已实现盈亏;locked 走重置额度) */
  activate: async (id: string) =>
    (await http.post<{ userId: string; status: string }>(`${P}/employees/${id}/activate`, {})).data,
  /**
   * 归档用户 / 移出名单(软删):先走 dismiss 编排(结算持仓 + 禁登录 + 清负责人指派),再打归档标记。
   * 历史分润 / 考核 / 成交全部保留可查;需原因(≥4 字)。
   */
  archive: async (id: string, body: { reason: string }) =>
    (
      await http.post<{ userId: string; status: string; archived: boolean; archivedAt: string }>(
        `${P}/employees/${id}/archive`,
        body,
        { skipAuthClear: true },
      )
    ).data,
  /** 撤销归档 → 回到「已停用」(仍禁登录);要恢复交易须再点「恢复」。 */
  unarchive: async (id: string, body: { reason: string }) =>
    (
      await http.post<{ userId: string; status: string; archived: boolean }>(
        `${P}/employees/${id}/unarchive`,
        body,
        { skipAuthClear: true },
      )
    ).data,
  /**
   * §6.4 提前解除该用户「当日风控冻结」(立即生效,riskPaused → false)。
   * 不动 status / balance;返回 resetAt,亏损风控从该时刻重新累计,不豁免当日规则。
   */
  riskResume: async (id: string, note?: string) =>
    (await http.post<{ userId: string; cleared: number; resetAt?: string | null }>(
      `${P}/employees/${id}/risk-resume`,
      note ? { note } : {},
    )).data,
  /** 账面贡献(平台口径)按交易日趋势(蓝图 §3.2) */
  contributionTrend: async (id: string, days = 30) =>
    (await http.get<ContributionTrend>(`${P}/users/${id}/contribution-trend`, { params: { days } })).data,
  /** 淘汰建议阈值(蓝图 §9):读当前生效值 */
  elimConfig: async () => (await http.get<ElimConfig>(`${P}/elim-config`)).data,
  /** 淘汰建议阈值:更新(即时生效,各项省略=不改) */
  updateElimConfig: async (body: UpdateElimConfigRequest) =>
    (await http.put<ElimConfig>(`${P}/elim-config`, body)).data,
};

// ─── 雇员/用户操作别名(/employees/* 命名口径,语义同 adminUsers)──────
export const adminEmployees = {
  /** §6.4 提前解除该用户「当日风控冻结」(立即生效);见 adminUsers.riskResume。 */
  riskResume: adminUsers.riskResume,
};

// ─── 品种 / 预设 ─────────────────────────────────────────────────

export const adminSymbols = {
  paramPresets: async () => (await http.get<ParamPreset[]>(`${P}/param-presets`)).data,
  ctraderCatalog: async (): Promise<CtraderSymbolCatalogItem[]> =>
    (await http.get<CtraderSymbolCatalogItem[]>(`${P}/ctrader/symbol-catalog`)).data,
  list: async () => (await http.get<AdminSymbol[]>(`${P}/symbols`)).data,
  create: async (body: CreateSymbolRequest) => (await http.post<void>(`${P}/symbols`, body)).data,
  update: async (id: string, body: UpdateSymbolRequest) => (await http.patch<void>(`${P}/symbols/${id}`, body)).data,
  setPreset: async (id: string, body: SetPresetRequest) => (await http.patch<void>(`${P}/symbols/${id}/preset`, body)).data,
  // A2 同向集中度封顶阈值(即时生效,不走 pending)。
  setDirectionalCap: async (id: string, body: SetDirectionalCapRequest) =>
    (await http.patch<void>(`${P}/symbols/${id}/directional-cap`, body)).data,
  // §16 交易服务费费率配置(即时生效,不走 pending)。
  setCommission: async (id: string, body: SetCommissionRequest) =>
    (await http.patch<void>(`${P}/symbols/${id}/commission`, body)).data,
  cancelPending: async (id: string) => (await http.delete<void>(`${P}/symbols/${id}/pending`)).data,
  applyPendingNow: async (id: string, body: ApplyPendingNowRequest) =>
    (await http.post<void>(`${P}/symbols/${id}/apply-pending-now`, body)).data,
  disable: async (id: string) => (await http.delete<void>(`${P}/symbols/${id}`)).data,
  enable: async (id: string) => (await http.post<void>(`${P}/symbols/${id}/enable`, {})).data,
};

// ─── 时段 ────────────────────────────────────────────────────────

/** DELETE /trading-session、/risk-config 撤销待生效的响应 */
export interface RevokePendingResponse {
  status: 'revoked';
  clearedKeys: string[];
}

export const adminSession = {
  get: async () => (await http.get<TradingSession>(`${P}/trading-session`)).data,
  update: async (body: UpdateTradingSessionRequest) =>
    (
      await http.patch<{ status: string; effectiveAt: string; validation?: SessionWindowValidation }>(
        `${P}/trading-session`,
        body,
      )
    ).data,
  /** §6.6 撤销 session.* 待生效变更(清空 pending_*) */
  revokePending: async () => (await http.delete<RevokePendingResponse>(`${P}/trading-session`)).data,
  /** §9.10 立即应用时段待生效变更(★ 绕过次日 03:05,需 TOTP + reason) */
  applyPendingNow: async (body: ApplyPendingNowRequest) =>
    (await http.post<{ status: string; appliedKeys: string[] }>(`${P}/trading-session/apply-now`, body)).data,
};

// ─── 交易时段组(账户分层)────────────────────────────────────────
//
// 组只决定「什么时候能交易」,不决定「算哪一天」——交易日界仍是全局兜底翻篇。
// 窗口类改动默认落待生效(次日全局兜底 +5 应用);name/enabled/isDefault 恒立即生效。
// 组的收盘是**分组日终结算**(只结算本组用户的虚拟腿,真实层交由净额对账收敛),
// 与平台级**全局强平**(置 emergency_stop、平主账户全部真实仓)是两回事,勿混。
export const adminTradingGroups = {
  /** 列表(每组带在组人数 userCount + 已绑定邀请码;另返回未分组人数 ungroupedUserCount) */
  list: async () => (await http.get<TradingGroupList>(`${P}/trading-groups`)).data,
  /** 新建(★ 立即生效 —— 新建时组内尚无用户,无需走待生效) */
  create: async (body: CreateTradingGroupRequest) =>
    (await http.post<TradingGroup>(`${P}/trading-groups`, body)).data,
  /** 修改(默认落待生效;body.applyNow=true 立即生效,需 reason) */
  update: async (id: string, body: UpdateTradingGroupRequest) =>
    (await http.patch<TradingGroup>(`${P}/trading-groups/${id}`, body)).data,
  /** 删除(仍有邀请码 / 用户绑定 → 409 GROUP_IN_USE,不做静默解绑) */
  remove: async (id: string) => (await http.delete<void>(`${P}/trading-groups/${id}`)).data,
  /** 紧急覆盖 — 立即应用该组待生效配置(需 TOTP + reason);只作用于本组 */
  applyPendingNow: async (id: string, body: ApplyPendingNowRequest) =>
    (await http.post<TradingGroup>(`${P}/trading-groups/${id}/apply-pending-now`, body)).data,
};

export const adminRiskConfig = {
  get: async () => {
    // 后端旧构建可能不返回 feishuChatIds(空=推送到 .env 默认群);契约里它是必填数组,
    // 这里在边界统一兜底成 [],避免 RiskConfig 页面 dirty/渲染处读 undefined.length 崩溃。
    // §6.4.1/§6.4.2 逐用户四金额 + 交易/持仓限制 + $30 门槛,旧后端缺省时边界兜底成迁移 0048 默认值,
    // 避免 RiskConfig 页面读 undefined 崩溃 / dirty 误判。
    const data = (await http.get<RiskConfig>(`${P}/risk-config`)).data;
    const withDailyLoss = <T extends Partial<RiskConfig>>(c: T): T => ({
      ...c,
      userDailyLossEnabled: c.userDailyLossEnabled ?? true,
      userDailyLossCloseUsd: c.userDailyLossCloseUsd ?? 200,
      userDailyLossFreezeUsd: c.userDailyLossFreezeUsd ?? 300,
      userDailyProfitEnabled: c.userDailyProfitEnabled ?? true,
      userDailyProfitCloseUsd: c.userDailyProfitCloseUsd ?? 200,
      userDailyProfitFreezeUsd: c.userDailyProfitFreezeUsd ?? 0,
      userMaxPositionLots100: c.userMaxPositionLots100 ?? 20,
      userDailyMaxTrades: c.userDailyMaxTrades ?? 5,
      manualCloseMinProfitUsd: c.manualCloseMinProfitUsd ?? 30,
    });
    return {
      ...withDailyLoss(data),
      feishuChatIds: data.feishuChatIds ?? [],
      pendingChange: data.pendingChange
        ? { ...withDailyLoss(data.pendingChange), feishuChatIds: data.pendingChange.feishuChatIds ?? [] }
        : data.pendingChange,
    };
  },
  update: async (body: UpdateRiskConfigRequest) => (await http.patch<void>(`${P}/risk-config`, body)).data,
  /** §6.6 撤销 risk.* 待生效变更(清空 pending_*) */
  revokePending: async () => (await http.delete<RevokePendingResponse>(`${P}/risk-config`)).data,
  /** §9.11 立即应用风控待生效变更(★ 绕过次日 03:05,需 TOTP + reason) */
  applyPendingNow: async (body: ApplyPendingNowRequest) =>
    (await http.post<{ status: string; appliedKeys: string[] }>(`${P}/risk-config/apply-now`, body)).data,
};

// ─── 用户考核(逐用户每交易日判定:全局默认 + 按用户覆盖 + 记录查看/改判)──────
//
// 契约已冻结(后端已实现)。金额字段 Money 直接透传(value=分,digits=2)。
// 改判 / 撤销改判需 reason(≥4 字)。
export const adminAssessment = {
  /** 全局默认考核配置。 */
  getConfig: async () => (await http.get<AssessmentConfig>(`${P}/assessment/config`)).data,
  /** 更新全局默认考核配置(即时生效)。 */
  updateConfig: async (body: AssessmentConfig) =>
    (await http.put<AssessmentConfig>(`${P}/assessment/config`, body)).data,
  /** 某用户的考核配置(effective + global + hasOverride)。 */
  getUserConfig: async (userId: string) =>
    (await http.get<UserAssessmentConfig>(`${P}/assessment/users/${userId}/config`)).data,
  /** 为某用户设置覆盖配置。 */
  setUserConfig: async (userId: string, body: AssessmentConfig) =>
    (await http.put<UserAssessmentConfig>(`${P}/assessment/users/${userId}/config`, body)).data,
  /** 清除某用户覆盖,恢复全局默认。 */
  clearUserConfig: async (userId: string) =>
    (await http.delete<UserAssessmentConfig>(`${P}/assessment/users/${userId}/config`)).data,
  /** 考核记录分页(status ∈ passed|failed|overridden)。 */
  records: async (query?: AssessmentRecordQuery) =>
    (await http.get<AssessmentRecordList>(`${P}/assessment/records`, { params: query })).data,
  /** 手动改判某用户某交易日的考核结果(需 reason)。 */
  override: async (userId: string, date: string, body: AssessmentOverrideRequest) =>
    (await http.post<void>(`${P}/assessment/records/${userId}/${date}/override`, body)).data,
  /** 撤销某用户某交易日的改判,恢复系统判定(需 reason)。 */
  revokeOverride: async (userId: string, date: string, body: AssessmentOverrideRevokeRequest) =>
    (await http.delete<void>(`${P}/assessment/records/${userId}/${date}/override`, { data: body })).data,
};

// ─── 应急 ────────────────────────────────────────────────────────

export const adminEmergency = {
  emergencyStop: async (body: EmergencyStopRequest) => (await http.post<void>(`${P}/emergency-stop`, body)).data,
  clearEmergencyStop: async (body: ClearEmergencyStopRequest) =>
    (await http.delete<void>(`${P}/emergency-stop`, { data: body })).data,
  forceLiquidation: async (body: ForceLiquidationRequest) => (await http.post<void>(`${P}/force-liquidation`, body)).data,
  clearTradeLock: async (body: ClearTradeLockRequest) =>
    (await http.delete<void>(`${P}/trade-lock`, { data: body })).data,
};

// ─── 跟单审计 / cTID ─────────────────────────────────────────────

export const adminAudit = {
  signals: async (params?: SignalListQuery) => (await http.get<SignalList>(`${P}/signals`, { params })).data,
  ctids: async () => (await http.get<CtidListResponse>(`${P}/ctids`)).data,
  startCtidOAuth: async (body: CtidOAuthStartRequest) =>
    (await http.post<CtidOAuthStartResponse>(`${P}/ctids/oauth/start`, body)).data,
  completeCtidOAuth: async (body: CtidOAuthCompleteRequest) =>
    (await http.post<CtidOAuthCompleteResponse>(`${P}/ctids/oauth/complete`, body)).data,
  syncCtidAccounts: async (id: string) =>
    (await http.post<CtidAccountSyncResponse>(`${P}/ctids/${id}/sync-accounts`)).data,
  refreshCtidToken: async (id: string) =>
    (await http.post<CtidRefreshTokenResponse>(`${P}/ctids/${id}/refresh-token`)).data,
  deleteCtid: async (id: string) => (await http.delete<CtidDeleteResponse>(`${P}/ctids/${id}`)).data,
  selectMainLiveAccount: async (accountId: string, body: SelectMainLiveAccountRequest) =>
    (await http.post<SelectMainLiveAccountResponse>(`${P}/ctids/accounts/${accountId}/select-main`, body)).data,
};

// ─── 全部订单 / 成交流水(真实账户逐笔成交)─────────────────────

export const adminDeals = {
  list: async (params?: DealListQuery) => (await http.get<DealList>(`${P}/deals`, { params })).data,
  reconcile: async (dealId: string) =>
    (await http.get<DealReconcile>(`${P}/deals/${dealId}/reconcile`)).data,
};

// ─── 用户订单(App 用户虚拟账户:下单/持仓/已平)─────────────────

export const adminUserOrders = {
  list: async (params?: UserOrderListQuery) =>
    (await http.get<UserOrderList>(`${P}/user-orders`, { params })).data,
};

// ─── §9.14 告警中心 — 历史风控事件查询 + 确认落库 ───────────────

export const adminRiskEvents = {
  list: async (query?: RiskEventListQuery) =>
    (await http.get<RiskEventList>(`${P}/risk-events`, { params: query })).data,
  ack: async (id: string) => (await http.post<RiskEvent>(`${P}/risk-events/${id}/ack`)).data,
};

// ─── §9.13 / §9.15 / §9.16 扩展端点 ─────────────────────────────

export const adminAuditLogs = {
  list: async (params?: AuditLogQuery) => (await http.get<AuditLogList>(`${P}/audit-logs`, { params })).data,
};

export const adminReports = {
  daily: async (): Promise<DailyReportRow[]> => (await http.get<DailyReportRow[]>(`${P}/reports/daily`)).data,
  weekly: async (): Promise<WeeklyReportPayload> => (await http.get<WeeklyReportPayload>(`${P}/reports/weekly`)).data,
  /**
   * 分组日报(交易时段组 × 交易日)。数据源 = group_pnl_daily 落表(收盘后随 daily_pnl cron 写);
   * **当前交易日**由后端实时补聚合返回,是截至此刻的**中间值**,当晚收盘结算完仍会变动。
   * 组归属取开仓时的 virtual_legs.trading_group_id 快照,故改绑渠道不会让历史数字漂移。
   * realizedNet 是 Money(digits=2),直接 fmtMoney,**不要**再 ÷100。
   */
  groupDaily: async (params?: GroupDailyQuery): Promise<GroupDailyReport> =>
    (await http.get<GroupDailyReport>(`${P}/reports/group-daily`, { params })).data,
  // v3 §2.1/§11.1 月度分润(合作伙伴按虚拟账户已实现盈利 10%;仅已实现·无高水位线)
  // inviteCodeId 可选:按渠道(邀请码 id)过滤;省略=全部渠道。
  // includeArchived / includeDisabledChannels 后端默认均为 false(已归档人员、已停用渠道
  // 连人带金额一起剔除)。这里**只要显式给了值就原样传**(不能只在 false 时传 ——
  // 那样打开开关时 true 发不出去,页面与后端口径会对不上)。
  profitShareMonthly: async (
    year: number,
    month: number,
    inviteCodeId?: string,
    opts?: { include?: 'settled' | 'all'; includeArchived?: boolean; includeDisabledChannels?: boolean },
  ): Promise<ProfitShareMonthly> =>
    (
      await http.get<ProfitShareMonthly>(`${P}/profit-share/monthly`, {
        params: {
          year,
          month,
          ...(inviteCodeId ? { inviteCodeId } : {}),
          ...(opts?.include ? { include: opts.include } : {}),
          ...(opts?.includeArchived !== undefined ? { includeArchived: opts.includeArchived } : {}),
          ...(opts?.includeDisabledChannels !== undefined
            ? { includeDisabledChannels: opts.includeDisabledChannels }
            : {}),
        },
      })
    ).data,
  /** 某人当月核对明细:顶部逐日考核 + 下方逐笔结算 + 汇总(与报表逐分一致)。 */
  profitShareMonthlyUser: async (userId: string, year: number, month: number): Promise<ProfitShareUserDetail> =>
    (
      await http.get<ProfitShareUserDetail>(`${P}/profit-share/monthly/users/${userId}`, {
        params: { year, month },
      })
    ).data,
  /**
   * 批量记录「本月发不发、发多少」。纯账务标记,不打款不改余额。
   * 金额由后端重算写快照(computedShare),前端传的 paidAmount 只作实发额。
   */
  profitSharePayouts: async (
    year: number,
    month: number,
    items: ProfitSharePayoutItem[],
  ): Promise<{ updated: number; paidTotal: Money }> =>
    (
      await http.put<{ updated: number; paidTotal: Money }>(`${P}/profit-share/monthly/payouts`, {
        year,
        month,
        items,
      })
    ).data,
  /**
   * 下载月度分润 Excel(汇总 / 人员汇总 / 每人一页)。后端生成 xlsx 二进制,
   * 这里只负责触发浏览器下载(文件名从 Content-Disposition 取,取不到用回退名)。
   */
  profitShareExportXlsx: async (
    year: number,
    month: number,
    opts?: {
      inviteCodeId?: string;
      include?: 'settled' | 'all';
      includeArchived?: boolean;
      includeDisabledChannels?: boolean;
      perUserSheets?: boolean;
    },
  ): Promise<void> => {
    const res = await http.get<Blob>(`${P}/profit-share/monthly/export.xlsx`, {
      responseType: 'blob',
      params: {
        year,
        month,
        ...(opts?.inviteCodeId ? { inviteCodeId: opts.inviteCodeId } : {}),
        ...(opts?.include ? { include: opts.include } : {}),
        // 与 /monthly 同规则:显式给值就传,保证导出与页面同口径。
        ...(opts?.includeArchived !== undefined ? { includeArchived: opts.includeArchived } : {}),
        ...(opts?.includeDisabledChannels !== undefined
          ? { includeDisabledChannels: opts.includeDisabledChannels }
          : {}),
        ...(opts?.perUserSheets === false ? { perUserSheets: false } : {}),
      },
    });
    const disposition = String(res.headers?.['content-disposition'] ?? '');
    // filename*=UTF-8''<percent-encoded> 优先(中文名);否则回退固定名。
    const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
    const filename = star ? decodeURIComponent(star) : `月度分润-${year}-${String(month).padStart(2, '0')}.xlsx`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  profitShareConfig: async (): Promise<ProfitShareConfig> =>
    (await http.get<ProfitShareConfig>(`${P}/profit-share/config`)).data,
  updateProfitShareConfig: async (body: UpdateProfitShareConfigRequest): Promise<ProfitShareConfigUpdateResponse> =>
    (await http.put<ProfitShareConfigUpdateResponse>(`${P}/profit-share/config`, body)).data,
  // 平台经营只读概览(① 真实盈亏 / ② 商业模式指标 / ③ 基差诊断,三口径互不相加)
  platformPnl: async (): Promise<PlatformPnlResponse> => (await http.get<PlatformPnlResponse>(`${P}/platform-pnl`)).data,
};

export const adminSettings = {
  listAdmins: async () => (await http.get<AdminAccount[]>(`${P}/admins`)).data,
  createAdmin: async (body: { username: string; displayName?: string; password: string }) =>
    (await http.post<void>(`${P}/admins`, body)).data,
  deleteAdmin: async (id: string) => (await http.delete<void>(`${P}/admins/${id}`)).data,
  /** §9.16 重置管理员密码 — 生成临时明文密码(开发期返回,生产改 SMS/邮件下发) */
  resetAdminPassword: async (id: string) =>
    (await http.post<ResetAdminPasswordResponse>(`${P}/admins/${id}/reset-password`)).data,
  listIpWhitelist: async () => (await http.get<IpWhitelistEntry[]>(`${P}/ip-whitelist`)).data,
  addIpWhitelist: async (body: { cidr: string; note?: string }) => (await http.post<void>(`${P}/ip-whitelist`, body)).data,
  removeIpWhitelist: async (cidr: string) =>
    (await http.delete<void>(`${P}/ip-whitelist/${encodeURIComponent(cidr)}`)).data,
  listTemplates: async () => (await http.get<NotificationTemplate[]>(`${P}/notification-templates`)).data,
  updateTemplate: async (id: string, body: { template?: string; enabled?: boolean }) =>
    (await http.patch<void>(`${P}/notification-templates/${id}`, body)).data,
};

// ─── App 版本管理 ───────────────────────────────────────────────
//
// GET 读各平台版本策略;PUT 立即生效(写 system_config.app.version)。
// 抬高某平台 minSupportedVersion(=强制旧版本升级)影响面大,后端记入审计;
// 仅改 latestVersion / 文案 / 地址(不抬 min)影响较小。
// history 由后端在 latestVersion 变更时自动维护,前端只读不提交。
export const adminAppVersion = {
  get: async () => (await http.get<AppVersionConfig>(`${P}/app-version`)).data,
  update: async (body: UpdateAppVersionRequest) =>
    (await http.put<AppVersionConfig>(`${P}/app-version`, body)).data,
};

// 系统设置 — 注册邀请码(§9.16 渠道统计)
export const adminInviteCodes = {
  list: async (params?: { page?: number; pageSize?: number }) =>
    (await http.get<InviteCodeList>(`${P}/invite-codes`, { params })).data,
  /**
   * 拉取**全部**邀请码(渠道下拉专用):循环翻页突破单页 200 上限,避免超 200 个渠道后
   * 后续渠道无法被筛选/选择。渠道是有界集合,循环开销可忽略。
   */
  listAll: async (): Promise<InviteCode[]> => {
    const pageSize = 200;
    const all: InviteCode[] = [];
    // 500 页硬上限仅作循环安全兜底(=10 万渠道,远超真实规模),防后端异常导致死循环。
    for (let page = 1; page <= 500; page++) {
      const res = await http.get<InviteCodeList>(`${P}/invite-codes`, { params: { page, pageSize } });
      const items = res.data.items ?? [];
      all.push(...items);
      if (items.length < pageSize || all.length >= (res.data.total ?? all.length)) break;
    }
    return all;
  },
  create: async (body: CreateInviteCodeRequest) =>
    (await http.post<InviteCode>(`${P}/invite-codes`, body)).data,
  update: async (id: string, body: UpdateInviteCodeRequest) =>
    (await http.patch<InviteCode>(`${P}/invite-codes/${id}`, body)).data,
};
